import React, { useEffect, useRef, useState } from "react";
import { findAuthorizedLogitechWheel } from "../../utils/controller/logitech_g923_device";

/**
 * 게이밍 휠 연결 끊김 경고.
 *
 * 연결돼 있던 휠(G923)이 빠지면 화면 상단에 붉은 경고를 띄우고, 다시 연결되면 자동으로
 * 지운다(요청). USB 감시 서비스가 포트 전원을 다시 넣어 복구하는 동안에도 운전자가
 * 휠이 죽어 있다는 걸 바로 알아야 한다.
 *
 * - 휠 드라이버(LogitechG923Wheel)는 콕핏에서만 마운트되므로 그 상태를 쓰지 않고,
 *   navigator.hid 의 connect/disconnect 를 직접 듣는다 — 로그인·차량 목록·콕핏
 *   어디서 빠져도 같은 기준으로 뜬다. 화면 이동(드라이버 언마운트)으로 오경보도 없다.
 * - **이번 세션에 한 번이라도 연결된 적이 있을 때만** 띄운다. 휠 없이 키보드로 쓰는
 *   PC 에서 계속 경고가 떠 있으면 안 된다.
 * - G923 은 HID 컬렉션이 여러 개라 이벤트가 여러 번 온다. 이벤트 자체를 믿지 않고
 *   매번 getDevices() 로 "지금 휠이 있나"를 다시 본다.
 */
export function WheelDisconnectBanner() {
  const [present, setPresent] = useState<boolean | null>(null);
  const [seen, setSeen] = useState(false);
  const checkSeqRef = useRef(0);

  useEffect(() => {
    const hid = typeof navigator !== "undefined" ? (navigator as any).hid : undefined;
    if (!hid?.getDevices) {
      return undefined;
    }
    let cancelled = false;

    const recheck = async () => {
      const seq = ++checkSeqRef.current;
      try {
        const devices = await hid.getDevices();
        // 늦게 끝난 이전 조회가 최신 결과를 덮지 않게 한다.
        if (cancelled || seq !== checkSeqRef.current) {
          return;
        }
        const now = Boolean(findAuthorizedLogitechWheel(devices));
        setPresent(now);
        if (now) {
          setSeen(true);
        }
      } catch (_err) {
        // 조회 실패는 판단 보류 — 경고를 켜거나 끄지 않는다.
      }
    };

    void recheck();
    hid.addEventListener?.("connect", recheck);
    hid.addEventListener?.("disconnect", recheck);
    return () => {
      cancelled = true;
      hid.removeEventListener?.("connect", recheck);
      hid.removeEventListener?.("disconnect", recheck);
    };
  }, []);

  if (!seen || present !== false) {
    return null;
  }

  return (
    <div
      role="alert"
      aria-live="assertive"
      data-testid="wheel-disconnect-banner"
      style={{
        position: "fixed",
        top: 64,
        left: "50%",
        transform: "translateX(-50%)",
        zIndex: 9998,
        display: "flex",
        alignItems: "center",
        gap: 8,
        padding: "9px 18px",
        borderRadius: 999,
        background: "rgba(192, 38, 38, 0.95)",
        boxShadow: "0 6px 20px rgba(0, 0, 0, 0.45)",
        color: "#ffffff",
        font: "700 14px var(--aw-font, system-ui)",
        letterSpacing: "0.01em",
        pointerEvents: "none",
        whiteSpace: "nowrap",
      }}
    >
      <span aria-hidden="true">⚠</span>
      게이밍 휠 연결 끊김 — USB 연결을 확인하세요
      <small style={{ fontWeight: 600, opacity: 0.8 }}>(다시 연결되면 자동으로 사라집니다)</small>
    </div>
  );
}

export default WheelDisconnectBanner;
